/**
 * Scheduling's facility operations table (workforce operational cutover,
 * Monday 2026-09-21).
 *
 *   Facility · Last visit · Next visit · Assigned provider · Manifest ·
 *   Scheduling state · Credentialing · Blocker · Open
 *
 * with the commands Schedule · Reschedule · Assign provider · Confirm ·
 * Complete · Cancel · Record / clear blocker. This replaces the daily
 * "Scheduling Board" use of Monday. Every dated cell says where its value
 * came from — the board's mirror (twice daily) or eMerge's own record — and a
 * native plan is shown ahead of the mirror. Credentialing is shown
 * prominently and enforces nothing (CRED-RULING-001). "Completed" is
 * Scheduling's record that the visit happened as planned; it is not the
 * clinical visit fact and opens no downstream work, and the panel says so.
 *
 * Nothing here names a resident. Buildings, providers and dates only.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FilledStatusChip from '../../components/status/FilledStatusChip';
import { SCHEDULING_STATUS, SNF_CREDENTIALING } from '../../theme/mondayPalette';
import {
  schedulingApi, outcomeOf, shortDate, freshKey, SchedulingScope, FacilityRow, FacilityTable, FacilityDetail, FacilityLens, FACILITY_LENSES, FACILITY_LENS_LABEL,
  ProviderOption, PlanCommand, PLAN_STATE_LABEL, stopWords, VisitFact, NextPlan, FacilityProvider, CoverageType, COVERAGE_TYPES,
  PORTAL_ACCESS_QUIET,
} from '../finance/financeApi';
import type { CoverageAccessOutcome, PlanOutcome } from '../finance/financeApi';
import ProviderAccess, { PORTAL_ACCESS_WORDS } from './ProviderAccess';
import { closureSentence } from '../provider/providerApi';
import { AddBuildingButton, EditBuildingSection, useBuildingMeta } from './BuildingEditor';
import { ReconciliationButton } from './BuildingReconciliation';
import type { BuildingMeta } from './buildingsApi';
import FacilityContactsSection from '../../components/facilityContacts/FacilityContactsSection';
import type { ContactDirectory } from '../../components/facilityContacts/facilityContactsApi';

/**
 * The building's detail also carries its contact directory (backend facilityOperations.detail):
 * whole for a caller who keeps contacts, the call sheet otherwise; `directory_error` when it could not be read.
 */
type DetailWithDirectory = FacilityDetail & { directory?: ContactDirectory | null; directory_error?: boolean };

/**
 * Who covers the building (readiness audit P1 #8): the coverage record the
 * admin board decides, now decidable by the Scheduling role. A proposal is
 * confirmed or rejected (with a reason); a live assignment is ended (with a
 * reason); a provider can be assigned directly. Nothing here is a visit —
 * the visit's provider lives on the plan above.
 */
function CoverageSection({ facilityId, facilityName, providers, options, canPlan, onChanged, closedNote = null }: { facilityId: string; facilityName: string; providers: FacilityProvider[]; options: ProviderOption[]; canPlan: boolean; onChanged: (msg: string) => Promise<void>;
  /** The building is closed for scheduling (2026-10-14): no coverage is added or confirmed; reject and end stay (the cleanup). */
  /**
   * The building is closed FOR PROVIDERS (ruling 2026-10-19: closed for scheduling, merged, or marked Dropped), as the
   * accurate sentence for its cause. The server refuses assigning or confirming coverage there (409), so neither is offered.
   */
  closedNote?: string | null }) {
  const [act, setAct] = useState<{ kind: 'reject' | 'end' | 'assign'; id?: string } | null>(null);
  const [reason, setReason] = useState('');
  const [provider, setProvider] = useState('');
  const [type, setType] = useState<CoverageType>('PRIMARY');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Whose portal access panel is open (ruling 2026-10-19: covering grants access; a manager links the login). */
  const [accessFor, setAccessFor] = useState<string | null>(null);
  const run = async (fn: () => Promise<{ success: boolean; code?: string; error?: string; message?: string; status?: number; data?: { access?: CoverageAccessOutcome } }>, done: string) => {
    setBusy(true); setErr(null);
    try {
      const r = await fn();
      if (!r.success) { setErr(outcomeOf(r, { wrote: true })); return; }
      setAct(null); setReason(''); setProvider('');
      // What the write did to the provider's portal access, in the server's words (e.g. waiting on the login link).
      const accessWords = r.data?.access?.message ? ` ${r.data.access.message}` : '';
      await onChanged(`${facilityName}: ${done}.${accessWords}`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <>
      <h3>Coverage</h3>
      {providers.length === 0 ? <p className="um-quiet">No provider assigned to this building.</p> : (
        <ul aria-label="Coverage">
          {providers.map((p) => (
            <li key={p.assignment_id}>
              {p.name || 'Unnamed'} · {p.coverage_type.toLowerCase()} · {p.coverage}
              {p.status === 'ACTIVE' && p.portal_access && (
                <span className={`sc-src${p.portal_access === 'IN_EFFECT' ? ' emerge' : ''}`}>{PORTAL_ACCESS_WORDS[p.portal_access]}</span>
              )}
              {canPlan && p.status === 'ACTIVE' && p.portal_access !== undefined && (
                <button type="button" className="um-open" style={{ marginLeft: 8 }} aria-pressed={accessFor === p.employee_id}
                  onClick={() => setAccessFor(accessFor === p.employee_id ? null : p.employee_id)}
                  aria-label={`Portal access for ${p.name || 'provider'}`}>
                  {p.portal_access === 'WAITING_ON_LOGIN_LINK' ? 'Link login' : 'Portal access'}
                </button>
              )}
              {canPlan && accessFor === p.employee_id && (
                <ProviderAccess employeeId={p.employee_id} providerName={p.name || 'this provider'}
                  onChanged={async (m) => { await onChanged(`${facilityName}: ${m}`); }} />
              )}
              {canPlan && p.status === 'PROPOSED' && (
                <span className="sc-cmds" style={{ display: 'inline-flex', marginLeft: 8 }}>
                  {!closedNote && <button type="button" className="um-open" disabled={busy} onClick={() => run(() => schedulingApi.decideCoverage(p.assignment_id, 'CONFIRM'), `${p.name || 'provider'} confirmed as ${p.coverage_type.toLowerCase()}`)} aria-label={`Confirm ${p.name || 'provider'} for ${facilityName}`}>Confirm</button>}
                  <button type="button" className="um-open" disabled={busy} aria-pressed={act?.kind === 'reject' && act.id === p.assignment_id} onClick={() => { setAct({ kind: 'reject', id: p.assignment_id }); setReason(''); setErr(null); }} aria-label={`Reject ${p.name || 'provider'} for ${facilityName}`}>Reject</button>
                </span>
              )}
              {canPlan && p.status === 'ACTIVE' && (
                <button type="button" className="um-open" style={{ marginLeft: 8 }} disabled={busy} aria-pressed={act?.kind === 'end' && act.id === p.assignment_id} onClick={() => { setAct({ kind: 'end', id: p.assignment_id }); setReason(''); setErr(null); }} aria-label={`End ${p.name || 'provider'} for ${facilityName}`}>End</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {closedNote && canPlan && <p className="um-quiet" role="note">{closedNote}: no provider is assigned or confirmed here. A proposal can still be rejected and coverage ended.</p>}
      {canPlan && !act && !closedNote && <div className="sc-cmds"><button type="button" className="um-open" onClick={() => { setAct({ kind: 'assign' }); setErr(null); }}>Assign coverage</button></div>}
      {canPlan && act && (act.kind === 'reject' || act.kind === 'end') && (
        <form className="um-form" aria-label={`${act.kind === 'reject' ? 'Reject coverage' : 'End coverage'} for ${facilityName}`} onSubmit={(e) => { e.preventDefault(); if (act.kind === 'reject') run(() => schedulingApi.decideCoverage(act.id!, 'REJECT', reason.trim()), 'proposal rejected'); else run(() => schedulingApi.endCoverage(act.id!, reason.trim()), 'coverage ended'); }}>
          <label><span>Why (required)</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="Coverage reason" /></label>
          <div className="btns">
            <button type="submit" className="um-open um-primary" disabled={busy || !reason.trim()}>{busy ? 'Recording…' : act.kind === 'reject' ? 'Confirm: Reject' : 'Confirm: End coverage'}</button>
            <button type="button" className="um-open" onClick={() => setAct(null)} disabled={busy}>Back</button>
          </div>
        </form>
      )}
      {canPlan && act && act.kind === 'assign' && !closedNote && (
        <form className="um-form" aria-label={`Assign coverage for ${facilityName}`} onSubmit={(e) => { e.preventDefault(); run(() => schedulingApi.assignCoverage({ facility_id: facilityId, provider_employee_id: provider, coverage_type: type }), 'coverage assigned'); }}>
          <ProviderSelect providers={options} value={provider} onChange={setProvider} label="Provider" />
          <label><span>Coverage type</span><select value={type} onChange={(e) => setType(e.target.value as CoverageType)} aria-label="Coverage type">{COVERAGE_TYPES.map((t) => <option key={t} value={t}>{t.toLowerCase()}</option>)}</select></label>
          <div className="btns">
            <button type="submit" className="um-open um-primary" disabled={busy || !provider}>{busy ? 'Recording…' : 'Confirm: Assign coverage'}</button>
            <button type="button" className="um-open" onClick={() => setAct(null)} disabled={busy}>Back</button>
          </div>
        </form>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </>
  );
}

/** A dated fact and its source, on two lines. */
function Fact({ fact, none }: { fact: VisitFact | null; none: string }) {
  if (!fact) return <span className="um-quiet">{none}</span>;
  return (
    <>
      {shortDate(fact.on)}{fact.state === 'TENTATIVE' ? ' (tentative)' : ''}
      <span className={`sc-src${fact.source === 'EMERGE' ? ' emerge' : ''}`}>{fact.source === 'EMERGE' ? 'eMerge' : 'board'} · {fact.what}</span>
    </>
  );
}

function ProviderSelect({ providers, value, onChange, label }: { providers: ProviderOption[]; value: string; onChange: (v: string) => void; label: string }) {
  return (
    <label>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">Not yet decided</option>
        {providers.map((p) => <option key={p.id} value={p.id}>{p.name || 'Unnamed'}{p.service_region ? ` · ${p.service_region}` : ''}{p.buildings_covered ? ` · covers ${p.buildings_covered}` : ''}</option>)}
      </select>
    </label>
  );
}

export type Command = 'schedule' | PlanCommand | 'blocker' | 'clear';
const COMMAND_LABEL: Record<Command, string> = {
  schedule: 'Schedule a visit', reschedule: 'Reschedule', provider: 'Assign provider', confirm: 'Confirm', complete: 'Complete visit', cancel: 'Cancel visit', details: 'Edit visit details', blocker: 'Record blocker', clear: 'Clear blocker',
};
/** The acts the server refuses on a building closed for scheduling (409 BUILDING_CLOSED, Isaiah Cotto 2026-10-14). */
const CLOSED_REFUSES: Command[] = ['schedule', 'reschedule', 'provider', 'confirm', 'blocker'];
/** The Kanban's words for a closure kind ("Closed for scheduling (dropped)"). */
const CLOSURE_WORDS: Record<string, string> = { DROPPED: 'dropped', DO_NOT_SCHEDULE: 'do not schedule' };

/**
 * One building, opened: the plan and its commands, the blocker, the
 * history, the contacts. Every command is a small form that names what it
 * needs and carries the plan's version.
 *
 * The body carries no table cell of its own (Tide phase 2), so it can sit in
 * this table's row (FacilityPanel below) and in the Scheduling board's
 * DetailRow. `row` is the table's row when there is one; the board opens it
 * with null and the panel reads everything from the building's detail, saying
 * "Loading" until it has it rather than offering a command it cannot judge.
 */
/**
 * `startWith` (the Kanban board, 2026-10-13): open this command's form once the building's detail is loaded -- a drop
 * only OPENS the act; the form, its checks and the confirm are this panel's own. A fresh `nonce` opens it again. Ignored
 * when the viewer cannot plan, or when the building has no plan/blocker for that command (the panel never shows a form it would not offer).
 */
export function FacilityPanelBody({ facilityId, name, row, canPlan, providers, onChanged, buildingMeta = null, startWith = null, onStartResult, readOnlyReason = null }: { facilityId: string; name: string; row: FacilityRow | null; canPlan: boolean; providers: ProviderOption[]; onChanged: (msg: string) => Promise<void>; buildingMeta?: BuildingMeta | null; startWith?: { command: Command; nonce: number } | null;
  /** What became of `startWith`: opened, swapped for Assign provider (a visit with no provider cannot be confirmed), or refused because the building's plan/blocker is not what the board showed. Said once per nonce. */
  onStartResult?: (r: { kind: 'opened' | 'swapped' | 'refused'; command: Command; opened?: Command; reason?: string }) => void;
  /** Replaces the "visits write is not open" line when the panel is read-only for another reason (a closed building). */
  readOnlyReason?: string | null }) {
  const [detail, setDetail] = useState<FacilityDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cmd, setCmd] = useState<Command | null>(null);
  const [date, setDate] = useState('');
  const [provider, setProvider] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  // Edit visit details: what the manager has typed. A field is sent only when it differs from the plan's own value.
  const [arrival, setArrival] = useState('');
  const [order, setOrder] = useState('');
  const [notesEdit, setNotesEdit] = useState<string | null>(null);

  // The building this panel is for, so a late response for another building is dropped.
  const current = useRef(facilityId);
  current.current = facilityId;
  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await schedulingApi.facility(facilityId);
      if (current.current !== facilityId) return;
      if (r.success && r.data) setDetail(r.data); else { setDetail(null); setErr(outcomeOf(r)); }
    } catch { if (current.current === facilityId) { setDetail(null); setErr('Could not reach the server.'); } }
  }, [facilityId]);
  // A different building starts with no detail and no open command: never the last building's.
  useEffect(() => { setDetail(null); setCmd(null); setFormErr(null); load(); }, [load]);

  const base: FacilityRow | null = detail || row;
  const plan: NextPlan | null = base ? base.next_plan : null;
  const blocker = base ? base.blocker : null;
  // Closed for scheduling (Isaiah Cotto, 2026-10-14): the server refuses schedule, reschedule, assign provider, confirm
  // and record blocker on a closed building (409 BUILDING_CLOSED). The panel never offers them there and says why;
  // cancel and complete (the cleanup of a plan left open, and the record of a visit made) and clear blocker stay.
  const closure = base ? base.closure || null : null;
  const closedWords = closure ? (CLOSURE_WORDS[closure.kind || ''] || closure.kind || 'reason not recorded') : null;
  // Coverage follows the provider-purposes closure (also merged, or marked Dropped); plans keep the board's own.
  const coverageClosedNote = base && base.provider_closure ? closureSentence(base.provider_closure)
    : closure ? `Closed for scheduling (${closedWords})` : null;
  const open = (c: Command) => { setCmd(c); setFormErr(null); setDate(''); setText(''); setProvider(c === 'provider' && plan ? (plan.provider_employee_id || '') : '');
    setArrival(c === 'details' && plan ? plan.arrival_time || '' : ''); setOrder(c === 'details' && plan && plan.route_order ? String(plan.route_order) : ''); setNotesEdit(null); };
  const started = useRef(0);
  useEffect(() => {
    if (!startWith || startWith.nonce === started.current || !canPlan || !base || base.id !== facilityId) return;
    started.current = startWith.nonce;
    const c = startWith.command;
    const why = closure && CLOSED_REFUSES.includes(c) ? `it is closed for scheduling (${closedWords})`
      : c === 'schedule' ? (plan ? `it now has an open plan (${PLAN_STATE_LABEL[plan.state].toLowerCase()})` : '')
      : c === 'clear' ? (blocker ? '' : 'it has no open blocker now')
      : c === 'blocker' ? (blocker ? 'it already has an open blocker' : '')
      : !plan ? 'it has no open visit plan now'
      : c === 'details' && plan.state !== 'PLANNED' && plan.state !== 'CONFIRMED' ? `the plan is already ${PLAN_STATE_LABEL[plan.state].toLowerCase()}`
      : c === 'confirm' && plan.state !== 'PLANNED' ? `the plan is already ${PLAN_STATE_LABEL[plan.state].toLowerCase()}` : '';
    if (why) { if (onStartResult) onStartResult({ kind: 'refused', command: c, reason: why }); return; }
    if (c === 'confirm' && plan && !plan.provider_employee_id) {
      open('provider');
      if (onStartResult) onStartResult({ kind: 'swapped', command: c, opened: 'provider' });
      return;
    }
    open(c);
    if (onStartResult) onStartResult({ kind: 'opened', command: c });
  });

  // The plan's instructions: the building's detail carries them (the board's next plan does not); until it loads the box starts empty.
  const savedNotes = (detail && plan ? detail.plans.find((p) => p.id === plan.id)?.notes : null) || '';
  const notesNow = notesEdit === null ? savedNotes : notesEdit;
  const detailsChanged = Boolean(plan) && (arrival !== (plan?.arrival_time || '') || order !== (plan?.route_order ? String(plan.route_order) : '') || notesNow !== savedNotes);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); if (!cmd) return;
    setBusy(true); setFormErr(null);
    try {
      let r;
      if (cmd === 'schedule') r = await schedulingApi.schedule(facilityId, { planned_on: date, provider_employee_id: provider || null, purpose: text.trim() || undefined, idempotency_key: freshKey() });
      else if (cmd === 'blocker') r = await schedulingApi.recordBlocker(facilityId, text.trim());
      else if (cmd === 'clear') r = await schedulingApi.clearBlocker(blocker!.id, text.trim() || undefined);
      else if (!plan) { setFormErr('There is no open visit plan to change.'); return; }
      else if (cmd === 'details') {
        const body: Record<string, unknown> = { version: plan.version };
        if (arrival !== (plan.arrival_time || '')) body.arrival_time = arrival || null;
        if (order !== (plan.route_order ? String(plan.route_order) : '')) body.route_order = order === '' ? null : Number(order);
        if (notesNow !== savedNotes) body.notes = notesNow.trim() === '' ? null : notesNow;
        r = await schedulingApi.planAction(plan.id, 'details', body);
      }
      else if (cmd === 'reschedule') r = await schedulingApi.planAction(plan.id, 'reschedule', { planned_on: date, reason: text.trim() || undefined, version: plan.version });
      else if (cmd === 'provider') r = await schedulingApi.planAction(plan.id, 'provider', { provider_employee_id: provider, version: plan.version });
      else if (cmd === 'confirm') r = await schedulingApi.planAction(plan.id, 'confirm', { note: text.trim() || undefined, version: plan.version });
      else if (cmd === 'complete') r = await schedulingApi.planAction(plan.id, 'complete', { completed_on: date || undefined, notes: text.trim() || undefined, version: plan.version });
      else r = await schedulingApi.planAction(plan.id, 'cancel', { reason: text.trim(), version: plan.version });
      if (!r.success) { setFormErr(outcomeOf(r, { wrote: true })); return; }
      setCmd(null);
      // A building marked Dropped (ruling 2026-11-03): say what the act did to the provider's portal when it needs saying,
      // e.g. "Visit recorded. Access pending: ... needs someone who holds provider.access.grant."
      const portal = (r.data as PlanOutcome | undefined)?.portal_access;
      const portalWords = portal && portal.words && !PORTAL_ACCESS_QUIET.includes(portal.outcome) ? ` ${portal.words}` : '';
      await onChanged(`${name}: ${COMMAND_LABEL[cmd].toLowerCase()} recorded.${portalWords}`);
      await load();
    } catch { setFormErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  const needsDate = cmd === 'schedule' || cmd === 'reschedule' || cmd === 'complete';
  const needsText = cmd === 'blocker' || cmd === 'cancel';
  const textLabel = cmd === 'schedule' ? 'Purpose (optional)' : cmd === 'reschedule' ? 'Why (optional)' : cmd === 'confirm' ? 'Note (optional)' : cmd === 'complete' ? 'Notes (optional)' : cmd === 'cancel' ? 'Why (required)' : cmd === 'blocker' ? 'Why this building cannot be scheduled' : 'Note (optional)';
  const disabled = busy || (cmd === 'details' && !detailsChanged) || (needsDate && cmd !== 'complete' && !date) || (needsText && !text.trim()) || (cmd === 'provider' && !provider);

  if (!base) {
    return (
      <div className="sc-panel" role="group" aria-label={`${name} operations`}>
        {err ? <p className="um-notice stop" role="alert">{err} <button type="button" className="um-open" onClick={load}>Try again</button></p> : <p className="um-quiet" role="status">Loading this building…</p>}
      </div>
    );
  }
  return (
      <div className="sc-panel" role="group" aria-label={`${name} operations`}>
        <div>
          <h3>Visit plan</h3>
          {plan ? (
            <p>
              <strong>{shortDate(plan.planned_on)}</strong> · {PLAN_STATE_LABEL[plan.state]} · {plan.provider_name || 'no provider yet'}{plan.purpose ? ` · ${plan.purpose}` : ''}
              {stopWords(plan.route_order, plan.arrival_time) ? ` · ${stopWords(plan.route_order, plan.arrival_time)}` : ''}
              {plan.expedited_request_id ? <span className="sc-tag attn" style={{ marginLeft: 6 }}>expedited</span> : null}
            </p>
          ) : <p className="um-quiet">No visit planned in eMerge. {base.next_visit ? `The board shows ${shortDate(base.next_visit.on)}${base.next_visit.state === 'TENTATIVE' ? ' (tentative)' : ''}.` : ''}</p>}
          {/* A host that already says why the panel is read-only (the Kanban's closed card) is not repeated. */}
          {closure && !readOnlyReason && (
            <p className="um-notice" role="note">
              Closed for scheduling ({closedWords}): no new visit, reschedule, provider, confirmation or blocker here.
              {canPlan && plan ? ' The open plan can still be cancelled, or completed if the visit happened.' : ''}
              {closure.reason ? <span className="sc-src">{closure.reason}</span> : null}
            </p>
          )}
          {canPlan && (
            <div className="sc-cmds">
              {!plan && !closure && <button type="button" className="um-open" aria-pressed={cmd === 'schedule'} onClick={() => open('schedule')}>{COMMAND_LABEL.schedule}</button>}
              {plan && (['reschedule', 'provider', ...(plan.state === 'PLANNED' ? ['confirm'] : []), 'complete', 'cancel', ...(plan.state === 'PLANNED' || plan.state === 'CONFIRMED' ? ['details'] : [])] as Command[]).filter((c) => !closure || !CLOSED_REFUSES.includes(c)).map((c) => (
                <button key={c} type="button" className="um-open" aria-pressed={cmd === c} onClick={() => open(c)}>{COMMAND_LABEL[c]}</button>
              ))}
              {blocker
                ? <button type="button" className="um-open" aria-pressed={cmd === 'clear'} onClick={() => open('clear')}>{COMMAND_LABEL.clear}</button>
                : !closure && <button type="button" className="um-open" aria-pressed={cmd === 'blocker'} onClick={() => open('blocker')}>{COMMAND_LABEL.blocker}</button>}
            </div>
          )}
          {canPlan && cmd && !(closure && CLOSED_REFUSES.includes(cmd)) && (
            <form className="um-form" onSubmit={submit} aria-label={`${COMMAND_LABEL[cmd]} for ${name}`}>
              {needsDate && <label><span>{cmd === 'complete' ? 'Visit date (today if blank)' : 'Visit date'}</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Visit date" /></label>}
              {(cmd === 'schedule' || cmd === 'provider') && <ProviderSelect providers={providers} value={provider} onChange={setProvider} label="Provider" />}
              {cmd === 'details' && (
                <>
                  <label><span>Arrival time (America/New_York)</span><input type="time" value={arrival} onChange={(e) => setArrival(e.target.value)} aria-label="Arrival time" />
                    {arrival !== '' && <button type="button" className="um-open" onClick={() => setArrival('')}>Clear arrival time</button>}</label>
                  <label><span>Route order (1 to 20)</span><input type="number" min={1} max={20} step={1} value={order} onChange={(e) => setOrder(e.target.value)} aria-label="Route order" />
                    {order !== '' && <button type="button" className="um-open" onClick={() => setOrder('')}>Clear route order</button>}</label>
                  <label><span>Instructions for the provider (shown on their My day page, never emailed)</span><textarea value={notesNow} onChange={(e) => setNotesEdit(e.target.value)} maxLength={2000} rows={4} aria-label="Instructions for the provider" /></label>
                </>
              )}
              {cmd !== 'details' && <label><span>{textLabel}</span><input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} aria-label={textLabel} /></label>}
              {cmd === 'complete' && <p className="um-quiet">This records that the visit happened as planned. It is Scheduling's record, not the clinical visit, and it starts nothing downstream.</p>}
              <div className="btns">
                <button type="submit" className="um-open um-primary" disabled={disabled}>{busy ? 'Recording…' : `Confirm: ${COMMAND_LABEL[cmd]}`}</button>
                <button type="button" className="um-open" onClick={() => setCmd(null)} disabled={busy}>Back</button>
              </div>
              {formErr && <p className="um-notice stop" role="alert">{formErr}</p>}
            </form>
          )}
          {!canPlan && <p className="um-quiet">{readOnlyReason || 'Read-only: the visits write is not open for this account.'}</p>}
        </div>
        <div>
          <h3>Blocker</h3>
          {blocker ? <p><strong>{blocker.reason}</strong><span className="sc-src">recorded {shortDate(blocker.recorded_at)}{blocker.recorded_by_name ? ` by ${blocker.recorded_by_name}` : ''}</span></p> : <p className="um-quiet">None open.</p>}
          {base.credentialing_status && <p><FilledStatusChip word={base.credentialing_status} column={SNF_CREDENTIALING} size="sm" /> <span className="sc-src">credentialing word (eMerge&rsquo;s, adopted from Monday) — shown, not enforced</span></p>}
          <CoverageSection facilityId={facilityId} facilityName={name} providers={base.providers} options={providers} canPlan={canPlan} closedNote={coverageClosedNote} onChanged={async (m) => { await onChanged(m); await load(); }} />
        </div>
        <div>
          <h3>History</h3>
          {err && <p className="um-notice stop" role="alert">{err}</p>}
          {!detail && !err && <p className="um-quiet">Loading…</p>}
          {detail && detail.plans.length === 0 && <p className="um-quiet">No visit plans recorded in eMerge yet.</p>}
          {detail && detail.plans.length > 0 && (
            <ul aria-label="Visit plans">
              {detail.plans.map((p) => <li key={p.id}>{shortDate(p.planned_on)} · {PLAN_STATE_LABEL[p.state]}{p.provider_name ? ` · ${p.provider_name}` : ''}{p.state === 'COMPLETED' && p.completed_on ? ` · done ${shortDate(p.completed_on)}` : ''}{p.state === 'CANCELLED' && p.cancelled_reason ? ` · ${p.cancelled_reason}` : ''}</li>)}
            </ul>
          )}
          {detail && detail.blockers.some((b) => b.cleared_at) && (
            <ul aria-label="Cleared blockers">
              {detail.blockers.filter((b) => b.cleared_at).map((b) => <li key={b.id} className="um-quiet">{b.reason} · cleared {shortDate(b.cleared_at!)}{b.cleared_by_name ? ` by ${b.cleared_by_name}` : ''}</li>)}
            </ul>
          )}
          <dl className="sc-facts">
            <dt>Last visit, every fact</dt>
            <dd>{base.last_visit_facts.length === 0 ? 'nothing recorded' : base.last_visit_facts.map((f) => `${shortDate(f.on)} (${f.source === 'EMERGE' ? 'eMerge' : 'board'}: ${f.what})`).join(' · ')}</dd>
            <dt>Board</dt>
            <dd>{base.scheduling_state.board || 'no scheduling row'}{base.scheduling_state.board_updated_at ? ` · mirrored ${shortDate(base.scheduling_state.board_updated_at)}` : ''}</dd>
          </dl>
        </div>
        {/* Facility contacts (2026-10-05 ruling): eMerge's records, editable here; the detail's copy is the read-only fallback. */}
        <FacilityContactsSection facilityId={facilityId} facilityName={name} fallback={detail ? (detail as DetailWithDirectory).directory || null : null}
          fallbackError={Boolean(err) || Boolean(detail && (detail as DetailWithDirectory).directory_error)} fallbackPending={!detail && !err} />
        <EditBuildingSection facilityId={facilityId} name={name} meta={buildingMeta} onChanged={async (m) => { await onChanged(m); await load(); }} />
      </div>
  );
}

/** This table's row panel: the body in a cell spanning the table's nine columns. */
function FacilityPanel({ row, canPlan, providers, onChanged, buildingMeta }: { row: FacilityRow; canPlan: boolean; providers: ProviderOption[]; onChanged: (msg: string) => Promise<void>; buildingMeta: BuildingMeta | null }) {
  return (
    <td colSpan={9} className="sc-panel-cell">
      <FacilityPanelBody facilityId={row.id} name={row.name} row={row} canPlan={canPlan} providers={providers} onChanged={onChanged} buildingMeta={buildingMeta} />
    </td>
  );
}

/** The manifest link eMerge holds; Monday's link only as a labelled reference (MAN-4). */
function ManifestCell({ manifest }: { manifest: FacilityRow['manifest'] }) {
  if (manifest.state === 'EMERGE' && manifest.url) {
    return (
      <>
        <a href={manifest.url} target="_blank" rel="noreferrer noopener">Cloud manifest ↗</a>
        <span className="sc-src emerge">{manifest.origin === 'MONDAY_IMPORT' ? 'eMerge · copied once from Monday' : 'eMerge'}</span>
      </>
    );
  }
  if (manifest.state === 'CLEARED') return <span className="um-quiet">no link (cleared in eMerge)</span>;
  if (manifest.monday_reference) {
    return (
      <>
        <span className="um-quiet">not set in eMerge</span>
        <span className="sc-src"><a href={manifest.monday_reference.url} target="_blank" rel="noreferrer noopener">Monday reference ↗</a> · reference only</span>
      </>
    );
  }
  return <span className="um-quiet">not set in eMerge</span>;
}

function Row({ row, open, onOpen, canPlan, providers, onChanged, buildingMeta }: { row: FacilityRow; open: boolean; onOpen: () => void; canPlan: boolean; providers: ProviderOption[]; onChanged: (msg: string) => Promise<void>; buildingMeta: BuildingMeta | null }) {
  const covering = row.providers.filter((p) => p.status === 'ACTIVE');
  const proposed = row.providers.filter((p) => p.status !== 'ACTIVE');
  return (
    <>
      <tr className={`${row.blocker ? 'blocked' : ''}${open ? ' open-row' : ''}`}>
        <td><strong>{row.name}</strong><span className="sc-src">{[row.city, row.state].filter(Boolean).join(', ')}{row.region ? ` · ${row.region}` : ''}</span></td>
        <td className="num"><Fact fact={row.last_visit} none="—" /></td>
        <td className="num"><Fact fact={row.next_visit} none="not scheduled" /></td>
        <td>
          {covering.length > 0 ? covering.map((p) => p.name || 'Unnamed').join(', ') : proposed.length > 0 ? <span className="um-quiet">{proposed.map((p) => p.name || 'Unnamed').join(', ')} <span className="sc-src">proposed, not decided</span></span> : <span className="um-quiet">none</span>}
          {row.next_plan && row.next_plan.provider_name && <span className="sc-src emerge">visit: {row.next_plan.provider_name}</span>}
        </td>
        <td><ManifestCell manifest={row.manifest} /></td>
        <td>
          {row.scheduling_state.native ? <span className="sc-tag ok">{PLAN_STATE_LABEL[row.scheduling_state.native]} in eMerge</span> : null}
          {row.scheduling_state.board ? <span style={{ marginLeft: row.scheduling_state.native ? 4 : 0 }}><FilledStatusChip word={row.scheduling_state.board} column={SCHEDULING_STATUS} size="sm" suffix={<span className="sc-src" style={{ display: 'inline' }}> board</span>} /></span> : null}
          {!row.scheduling_state.native && !row.scheduling_state.board && <span className="um-quiet">—</span>}
          {row.expedited_open > 0 && <span className="sc-tag attn" style={{ marginLeft: 4 }}>{row.expedited_open} expedited</span>}
        </td>
        <td>{row.credentialing_status ? <FilledStatusChip word={row.credentialing_status} column={SNF_CREDENTIALING} size="sm" /> : <span className="um-quiet">—</span>}</td>
        <td>{row.blocker ? <span className="sc-tag attn" title={row.blocker.reason}>{row.blocker.reason.length > 40 ? `${row.blocker.reason.slice(0, 40)}…` : row.blocker.reason}</span> : <span className="um-quiet">—</span>}</td>
        <td><button type="button" className="um-open" aria-expanded={open} onClick={onOpen} aria-label={`${open ? 'Close' : 'Open'} ${row.name}`}>{open ? 'Close' : 'Open'}</button></td>
      </tr>
      {open && <tr><FacilityPanel row={row} canPlan={canPlan} providers={providers} onChanged={onChanged} buildingMeta={buildingMeta} /></tr>}
    </>
  );
}

export default function FacilityOperations({ scope, onNotice }: { scope: SchedulingScope; onNotice: (msg: string) => void }) {
  const [lens, setLens] = useState<FacilityLens>('board');
  const [q, setQ] = useState('');
  const [typed, setTyped] = useState('');
  const [page, setPage] = useState(1);
  const [table, setTable] = useState<FacilityTable | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const canPlan = Boolean(scope.can.plan);
  const buildingMeta = useBuildingMeta();

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await schedulingApi.facilities({ q: q || null, lens, page });
      if (r.success && r.data) setTable(r.data); else { setTable(null); setErr(outcomeOf(r)); }
    } catch { setTable(null); setErr('Could not reach the server.'); }
  }, [q, lens, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canPlan) return;
    (async () => { try { const r = await schedulingApi.providers(); if (r.success && r.data) setProviders(r.data); } catch { /* the picker is empty; the server still refuses a bad id */ } })();
  }, [canPlan]);

  const lenses = useMemo(() => FACILITY_LENSES, []);
  const onChanged = async (msg: string) => { onNotice(msg); await load(); };
  // Show the building just added: every building, searched by its name.
  const onAdded = (b: { name: string }) => {
    onNotice(`${b.name} added. It is on the Scheduling board's Main table under "No open plan".`);
    setLens('all'); setTyped(b.name); setQ(b.name); setPage(1); setOpenId(null);
  };

  return (
    <section className="um-card" aria-label="Facility operations">
      <div className="sc-controls">
        <div className="sc-chips" role="group" aria-label="Which buildings">
          {lenses.map((l) => (
            <button key={l} type="button" className="um-open" aria-pressed={lens === l} title={scope.lenses ? scope.lenses[l] : undefined} onClick={() => { setLens(l); setPage(1); setOpenId(null); }}>
              {FACILITY_LENS_LABEL[l]}<span className="n">{table ? table.lenses[l] ?? '' : ''}</span>
            </button>
          ))}
        </div>
        <form className="sc-search" onSubmit={(e) => { e.preventDefault(); setQ(typed.trim()); setPage(1); }} role="search">
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Building name" aria-label="Search buildings" />
          <button type="submit" className="um-open">Search</button>
          {q && <button type="button" className="um-open" onClick={() => { setTyped(''); setQ(''); setPage(1); }}>Clear</button>}
        </form>
        <AddBuildingButton meta={buildingMeta} onAdded={onAdded} />
        <ReconciliationButton meta={buildingMeta} onNotice={(m) => { onNotice(m); load(); }} />
      </div>
      {err && <p className="um-notice stop" role="alert" style={{ margin: 12 }}>{err} <button type="button" className="um-open" onClick={load}>Try again</button></p>}
      {!err && !table && <p className="sc-empty">Loading…</p>}
      {table && table.items.length === 0 && <p className="sc-empty">{q ? `No building matches "${q}" in this view.` : 'Nothing in this view.'}</p>}
      {table && table.items.length > 0 && (
        <div className="sc-scroll" role="region" aria-label="Facility operations table" tabIndex={0}>
          <table className="sc-table">
            <thead>
              <tr>
                <th>Facility</th><th>Last visit</th><th>Next visit</th><th>Assigned provider</th><th>Manifest</th><th>Scheduling state</th><th>Credentialing</th><th>Blocker</th><th><span className="um-quiet">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {table.items.map((row) => <Row key={row.id} row={row} open={openId === row.id} onOpen={() => setOpenId(openId === row.id ? null : row.id)} canPlan={canPlan} providers={providers} onChanged={onChanged} buildingMeta={buildingMeta} />)}
            </tbody>
          </table>
        </div>
      )}
      {table && table.total > table.limit && (
        <div className="sc-pager">
          <span>{(table.page - 1) * table.limit + 1}–{Math.min(table.page * table.limit, table.total)} of {table.total}</span>
          <div className="btns">
            <button type="button" className="um-open" disabled={table.page <= 1} onClick={() => setPage(table.page - 1)}>Previous</button>
            <button type="button" className="um-open" disabled={table.page * table.limit >= table.total} onClick={() => setPage(table.page + 1)}>Next</button>
          </div>
        </div>
      )}
      <div className="sc-foot">
        <span><strong>Where each column comes from.</strong> "board" is Monday's scheduling mirror, refreshed twice a day; "eMerge" is what Scheduling recorded here. A visit planned here is shown ahead of the board's date.</span>
        <span>Credentialing is the building's word (eMerge's since 2026-10-05, adopted from Monday), shown prominently and enforcing nothing (CRED-RULING-001). A building's details are eMerge's; Monday no longer changes them. A blocker is Scheduling's own note on why a building is on hold.</span>
        <span>"Complete visit" is Scheduling's record that the visit happened as planned. The clinical visit and what it generates stay with the provider's workflow.</span>
      </div>
    </section>
  );
}
